import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import {
  HttpClient,
  HttpHeaders,
  HttpErrorResponse,
} from '@angular/common/http';
import { catchError, tap, map } from 'rxjs/operators';
import { Preferences } from '@capacitor/preferences';
import { Platform } from '@ionic/angular';
import { EnvironmentService } from './services/environment.service';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  version = '3.7.8';
  ifc_available = true;

  constructor(
    public platform: Platform,
    private http: HttpClient,
    public env: EnvironmentService
  ) {}

  /** API-Basis der aktiven Umgebung, z. B. https://.../api/ */
  get apiBase(): string {
    return this.env.apiBase;
  }

  /** Storage-Basis (Kurzform, .../) — für XKT-/IFC-Pfade, vgl. savestate.service. */
  get root(): string {
    return this.env.storageShort;
  }

  /** Uploads-Basis (.../uploads/). */
  get storageBase(): string {
    return this.env.storageBase;
  }

  /** Kompatibilität: true = nicht Live. Nur noch lesend. */
  get debug(): boolean {
    return this.env.current !== 'live';
  }

  ngOnInit() {}

  private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(
        `Backend returned code ${error.status}, ` + `body was: ${error.error}`
      );
    }
    // return an observable with a user-facing error message
    return throwError('Something bad happened; please try again later.');
  }

  private extractData(res: Response) {
    let body = res;
    return body || {};
  }

  public sendFeedback(feedback): Observable<any> {
    console.log();
    const url_base = this.apiBase;

    var data = {
      session: (window as any).session_key,
      text: feedback,
    };

    return this.http
      .post(url_base + 'feedback', data, httpOptions)
      .pipe(map(this.extractData), catchError(this.handleError));
  }

  public getProjects(): Observable<any> {
    var data = {
      session: (window as any).session_key,
    };

    const url_base = this.apiBase;

    return this.http
      .post(url_base + 'projects', data, httpOptions)
      .pipe(map(this.extractData), catchError(this.handleError));
  }

  public checkVersion() {
    const url_base = this.apiBase;

    var url = url_base + 'app/version/current';

    if (this.platform.is('ios')) {
      url += '_ios';
    } else {
      url += '_android';
    }

    return this.http
      .get(url, httpOptions)
      .pipe(map(this.extractData), catchError(this.handleError));
  }

  public pushNewImages(): Observable<any> {
    // Iterate over all new image and create a form date with an instruction array and an images array
    if (typeof (window as any).new_images != 'undefined') {
      // NOT YET DONE
      var data = {};

      const url_base = this.apiBase;

      return this.http
        .post(url_base + 'sync-images', data, httpOptions)
        .pipe(map(this.extractData), catchError(this.handleError));
    }
  }

  public checkExternalLogin(uuid): Observable<any> {
    var data = {};

    const url_base = this.apiBase;

    return this.http
      .post(url_base + 'sso_login/' + uuid, data, httpOptions)
      .pipe(map(this.extractData), catchError(this.handleError));
  }

  public pushDamages(damages, notes, pins = false): Observable<any> {
    var data = {
      session: (window as any).session_key,
      damages: damages,
      notes: notes,
      custompins: pins,
    };

    console.log('Push Damages: ', damages);
    console.log('Push Notes: ', notes);
    console.log('Push Pins: ', pins);

    const url_base = this.apiBase;

    return this.http
      .post(url_base + 'sync-damages', data, httpOptions)
      .pipe(map(this.extractData), catchError(this.handleError));
  }

  /**
   * Phase 1 des server-seitigen Marker-Resolvers: nach dem ersten
   * erfolgreichen Modell-Load berichtet der Viewer seine model.aabb
   * (im post-origin-shift Koordinatensystem) an den Server. Wird
   * dort gespeichert, falls noch keine aabb hinterlegt ist; spätere
   * abweichende Reports werden geloggt für Drift-Erkennung.
   *
   * Fire-and-forget — Fehler beeinflussen die Viewer-Funktionalität
   * nicht; sie loggen nur in der Konsole.
   */
  public reportIfcAabb(
    ifcfileId: any,
    aabb: number[],
    runtimeOffset?: number[]
  ): Observable<any> {
    var data: any = {
      session: (window as any).session_key,
      aabb: aabb,
    };
    if (runtimeOffset && runtimeOffset.length === 3) {
      data.runtime_offset = runtimeOffset;
    }
    const url_base = this.apiBase;
    return this.http
      .post(url_base + 'ifcfile/' + ifcfileId + '/aabb', data, httpOptions)
      .pipe(map(this.extractData), catchError(this.handleError));
  }

  public async pushDeletedBuildingPhotos(building: any) {
    building = JSON.parse(building);
    var data = {
      session: (window as any).session_key,
      deleted_photos: building.deleted_photos,
    };

    const url_base = this.apiBase;
    try {
      this.http
        .post<boolean>(
          url_base + 'building/' + building.id + '/picture/delete',
          data
        )
        .pipe(catchError((e) => this.handleError(e)))
        .subscribe(
          (data) => {
            console.log('Building Images successfully deleted');
            console.log(data);
          },
          (err) => {
            console.log(
              'Upload fehlgeschlagen - bitte prüfen Sie Ihre Internetverbindung.'
            );
            console.log('Error uploading Photo');
            console.log(err);
          },
          () => {
            console.log('Complete function triggered.');
          }
        );
    } catch (error) {
      console.log(
        'Bild konnte nicht verarbeitet werden und wird übersprungen.'
      );
    }
  }

  public async pushBuildingPhotoNotes(building: any) {
    building = JSON.parse(building);
    var data = {
      session: (window as any).session_key,
      pictures: building.data,
    };

    console.log('Building Data: ', building.data);

    const url_base = this.apiBase;
    try {
      this.http
        .post<boolean>(
          url_base + 'building/' + building.id + '/pictures/update',
          data
        )
        .pipe(catchError((e) => this.handleError(e)))
        .subscribe(
          (data) => {
            console.log('Building Image Notes successfully updated');
            console.log(data);
          },
          (err) => {
            console.log(
              'Upload fehlgeschlagen - bitte prüfen Sie Ihre Internetverbindung.'
            );
            console.log('Error uploading Photo Notes');
            console.log(err);
          },
          () => {
            console.log('Complete function triggered.');
          }
        );
    } catch (error) {
      console.log(
        'Gebäudefotonotiz konnte nicht verarbeitet werden und wird übersprungen.'
      );
    }
  }
}
